import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "field-scouting-records",
  kind: "domain-vertical",
  title: "Field Scouting & Crop Health Records",
  shortTitle: "Field Scouting",
  status: "draft",
  tier: "we-do",
  seoTitle: "Field Scouting & Crop Health Records | GA",
  seoDescription:
    "Mobile field scouting, pest and disease logging, weed pressure tracking, and crop health observations for agricultural decision-support.",
  summary:
    "Mobile-first field scouting logs, pest and disease severity ratings, weed pressure maps, and crop vigor records that replace lost memories with structured operational history.",
  image: "/images/blog/dashboard-design-hero.webp",
  relatedCapabilitySlugs: [],
  relatedIndustrySlugs: ["agriculture"],
  relatedPostSlugs: [
    "operational-task-management",
    "dashboard-design",
    "smart-forms-for-harsh-environments",
    "from-bed-grid-to-packing-shed",
  ],
  engineering: [
    "Mobile-first scouting forms optimized for one-handed in-field observation logging and photo capture",
    "Structured pest and disease classification schemas with standardized economic threshold ratings",
    "Weed flush mapping and pressure categorization (species, density, growth stage) tied to specific beds",
    "Crop vigor, canopy closure, and phenological stage progression logging across growing seasons",
    "Offline-first data sync architecture ensuring scouting notes are preserved in remote field locations",
    "Automated scout-to-task triggers generating immediate weeding, spraying, or rogueing work orders",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "Scouting Notes Are Perishable Operational Intelligence",
    },
    {
      type: "paragraph",
      text: "Detecting flea beetle pressure on brassica seedlings, downy mildew lesions on cucurbits, or an emerging flush of pigweed requires rapid operational intervention. When field observations remain trapped in a scout's personal notebook, casual text threads, or memory, management cannot act before economic threshold limits are exceeded and crop quality is compromised.",
    },
    {
      type: "heading",
      level: 2,
      text: "Transforming Field Observations into Structured Action",
    },
    {
      type: "paragraph",
      text: "Disciplined agricultural systems capture scouting data against specific spatial beds and crop successions using standardized severity metrics. When observations cross predefined intervention thresholds, the system immediately flags the issue on management dashboards and triggers targeted work orders—closing the gap between detecting a field problem and resolving it.",
    },
  ],
  phases: {
    study: {
      focus:
        "Audit in-field scouting practices, pest monitoring protocols, economic thresholds, and scout-to-manager communication flows.",
      produces: [
        "Field scouting workflow, target pest, and disease symptom inventory",
        "Economic injury level and action threshold classification criteria",
        "Field scouting frequency, sampling patterns, and mobile hardware review",
        "Historical crop loss and pest outbreak post-mortem analysis",
      ],
      fit: {
        good: [
          "Commercial growers and integrated pest management (IPM) practitioners needing structured field scouting records",
          "Farms seeking to replace unstructured notes and messaging apps with threshold-driven crop health logging",
        ],
        poor: [
          "Operations with no scouting program or interest in systematic crop health monitoring",
          "Farms expecting satellite imagery alone to replace close-range physical crop and pest inspection",
        ],
      },
    },
    "initial-design": {
      focus:
        "Architect observation data schemas, pest classification taxonomies, mobile scouting forms, and notification rules.",
      produces: [
        "Observation entity schema (scout event, pest/disease code, severity rating, photo attachments)",
        "Integrated pest management (IPM) threshold evaluation rules",
        "Mobile-optimized observation capture wireframes and quick-entry dials",
        "Automated work-order generation rules linked to scouting threshold breaches",
      ],
    },
    "detailed-design": {
      focus:
        "Specify offline database synchronization, image compression pipelines, mapping layers, and dashboard feeds.",
      produces: [
        "Offline-first storage schema and conflict-resolution contracts for field devices",
        "Client-side photo compression and geo-tagging specifications",
        "Field heatmap visualization and pest progression mapping algorithms",
        "Agronomic advisory and historical scouting query APIs",
      ],
    },
    implementation: {
      focus:
        "Build and deploy the scouting application, configure regional pest schemas, equip field scouts, and validate work-order triggers.",
      produces: [
        "Production field scouting and crop health recording application",
        "Pre-configured pest, weed, and disease classification reference catalogs",
        "Active scouting dashboards with threshold-alert pipelines",
        "Field scouting guidebooks and IPM data collection training materials",
      ],
    },
  },
};
